import type {
  AttendanceHistoryItem,
  Collaborator,
  CollaboratorClothing,
  CollaboratorEquipment,
  CollaboratorUnavailability,
  CollaboratorWorkTime,
  HrAnalyticsResponse,
  HrRemoteStorePeriodMetricsResponse,
  HrSalesPeaksResponse,
  HrStoreWeeklyMetricsResponse,
  PlanningConfig,
  WorkShift,
} from '@gozoki/api-types'
import { gozokiApiWithAuth } from '../gozokiApi'

// Fetch all collaborators
export const fetchCollaborators = async (storeId?: number) => {
  const response = await gozokiApiWithAuth.get<Collaborator[]>('/hr/collaborators', {
    params: { storeId },
  })
  return response.data
}

// Fetch one collaborator by id
export const fetchCollaborator = async (id: number) => {
  const response = await gozokiApiWithAuth.get<Collaborator>(`/hr/collaborators/${id}`)
  return response.data
}

// Fetch all work times for a collaborator
export const fetchCollaboratorWorkTimes = async (collaboratorId: number) => {
  const response = await gozokiApiWithAuth.get<CollaboratorWorkTime[]>(
    `/hr/collaborators/${collaboratorId}/work-times`,
  )
  return response.data
}

// Fetch all unavailabilities for a collaborator
export const fetchCollaboratorUnavailabilities = async (collaboratorId: number) => {
  const response = await gozokiApiWithAuth.get<CollaboratorUnavailability[]>(
    `/hr/collaborators/${collaboratorId}/unavailabilities`,
  )
  return response.data
}

// Fetch all equipment for a collaborator
export const fetchCollaboratorEquipment = async (collaboratorId: number) => {
  const response = await gozokiApiWithAuth.get<CollaboratorEquipment[]>(
    `/hr/collaborators/${collaboratorId}/equipment`,
  )
  return response.data
}

// Fetch all clothing for a collaborator
export const fetchCollaboratorClothing = async (collaboratorId: number) => {
  const response = await gozokiApiWithAuth.get<CollaboratorClothing[]>(
    `/hr/collaborators/${collaboratorId}/clothing`,
  )
  return response.data
}

export interface FetchWorkShiftsParams {
  storeId?: number
  date?: string
  dateStart?: string
  dateEnd?: string
  clockedOnly?: boolean
}

export interface FetchAvailableCollaboratorsParams {
  storeId: number
  date: string
  startTime: string
  endTime: string
}

export interface FetchAttendanceHistoryParams {
  storeId: number
}

export interface FetchHrAnalyticsParams {
  storeId?: number
  dateStart?: string
  dateEnd?: string
}

export interface FetchHrSalesPeaksParams {
  storeId: number
}

export interface FetchHrStoreWeeklyMetricsParams {
  storeId: number
  dateStart: string
  dateEnd: string
}

export interface FetchHrRemoteStorePeriodMetricsParams {
  storeId: number
  dateStart: string
  dateEnd: string
}

// Fetch all work shifts
export const fetchWorkShifts = async (params?: FetchWorkShiftsParams) => {
  const response = await gozokiApiWithAuth.get<WorkShift[]>('/hr/work-shifts', {
    params,
  })
  return response.data
}

// Fetch collaborators available for a shift window
export const fetchAvailableCollaborators = async (params: FetchAvailableCollaboratorsParams) => {
  const response = await gozokiApiWithAuth.get<Collaborator[]>('/hr/collaborator/available', {
    params,
  })
  return response.data
}

export const fetchAttendanceHistory = async (params: FetchAttendanceHistoryParams) => {
  const response = await gozokiApiWithAuth.get<AttendanceHistoryItem[]>('/hr/attendances/history', {
    params,
  })
  return response.data
}

export const fetchHrAnalytics = async (params?: FetchHrAnalyticsParams) => {
  const response = await gozokiApiWithAuth.get<HrAnalyticsResponse>('/hr/analytics', {
    params,
  })
  return response.data
}

export const fetchHrSalesPeaks = async (params?: FetchHrSalesPeaksParams) => {
  const response = await gozokiApiWithAuth.get<HrSalesPeaksResponse>('/hr/analytics/sales-peaks', {
    params,
  })
  return response.data
}

export const fetchHrStoreWeeklyMetrics = async ({
  storeId,
  dateStart,
  dateEnd,
}: FetchHrStoreWeeklyMetricsParams) => {
  const response = await gozokiApiWithAuth.get<HrStoreWeeklyMetricsResponse>(
    `/hr/store-metrics/${storeId}/period`,
    {
      params: {
        dateStart,
        dateEnd,
      },
    },
  )
  return response.data
}

export const fetchHrRemoteStorePeriodMetrics = async ({
  storeId,
  dateStart,
  dateEnd,
}: FetchHrRemoteStorePeriodMetricsParams) => {
  const response = await gozokiApiWithAuth.get<HrRemoteStorePeriodMetricsResponse>(
    `/hr/remote-store-metrics/${storeId}/period`,
    {
      params: {
        dateStart,
        dateEnd,
      },
    },
  )
  return response.data
}

// Fetch one work shift by id
export const fetchWorkShift = async (id: number) => {
  const response = await gozokiApiWithAuth.get<WorkShift>(`/hr/work-shifts/${id}`)
  return response.data
}

// Fetch planning configuration (global + per-store)
export const fetchPlanningConfig = async () => {
  const response = await gozokiApiWithAuth.get<PlanningConfig>('/hr/planning/config')
  return response.data
}
